import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";

import { Badge, Box, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";

import ArticleCard from "./ArticleCard";
import { Article } from "@/components/planning/Project";
import ApiService from "@/api/ApiService";

const getFilter = (searchField: string) => {
  const searchTerms = searchField
    .split(" ")
    .map((x) => x.toLocaleLowerCase().trim().replace(/_/g, " "))
    .filter((x) => !!x.length);

  if (!searchTerms.length) return (fields: string[]) => true;

  return (fields: string[]) => {
    for (let term of searchTerms) {
      let found = false;
      for (let value of fields) {
        if (value.toLocaleLowerCase().includes(term)) {
          found = true;
          break;
        }
      }
      if (!found) return false;
    }
    return true;
  };
};

export default function NewBauhofSelector({
  abort,
  plant,
  projectId,
  selected,
  onSelect,
}: {
  onSelect: (selected: [Article, number][]) => void;
  selected: { [id: number]: number };
  projectId: number;
  plant: number;
  abort?: () => void;
}) {
  const [searchField, setSearchField] = useState("");

  const [selection, setSelection] = useState<{ [id: number]: number }>({});

  useEffect(() => setSelection(selected), [selected]);

  const [open, setOpen] = useState<string[]>([]);

  const toggleOpen = (category: string) => {
    setOpen((prevOpen) => (prevOpen.includes(category) ? prevOpen.filter((item) => item !== category) : [...prevOpen, category]));
  };

  const bauhofArticleQuery = useQuery({
    queryKey: ["getBauhofArticles2", "" + projectId],
    queryFn: ApiService.getBauhofArticles(projectId),
  });

  const articlesUnfiltered = useMemo(() => {
    if (!bauhofArticleQuery.isSuccess) return [];
    const a: any[] = [];

    for (let bauhof of bauhofArticleQuery.data as any[]) {
      if (bauhof.id !== plant) continue;
      for (let article of bauhof.articles) {
        a.push({ ...article, category: article.category || "Ohne Kategorie" });
      }
    }

    return a.sort((x, y) => x.category.localeCompare(y.category) || x.name.localeCompare(y.name));
  }, [bauhofArticleQuery.data, bauhofArticleQuery.isSuccess, plant]);

  const articles = useMemo(() => {
    const filter = getFilter(searchField);
    return articlesUnfiltered.filter((x) => filter([x.category, x.name]));
  }, [searchField, articlesUnfiltered]);

  const selectedArticles = useMemo(
    () => articles.filter((x) => x.id in selection).map((x) => [x, selection[x.id]] as [Article, number]),
    [articles, selection],
  );

  const categoryCounts = useMemo(() => {
    const cc: { [cat: string]: number } = {};
    for (let [article, count] of selectedArticles) {
      const cato = (article.category || "").split(";");
      let ff = "";
      for (let i = 0; i < cato.length; i++) {
        ff += (i > 0 ? ";" : "") + cato[i];
        if (!(ff in cc)) cc[ff] = 0;
        cc[ff] += count;
      }
    }
    return cc;
  }, [selectedArticles]);

  const [elements, sticky] = useMemo(() => {
    const e: React.ReactNode[] = [];
    const s: number[] = [];
    let garundu: React.ReactNode[] = [];
    let lastCat = "";
    let lastCatArr: string[] = [];
    let visible = false;

    for (let article of articles as any[]) {
      if (lastCat !== article.category) {
        visible = true;
        const cat = (article.category || "").split(";");
        let scat = "";
        let lcat = "";

        if (garundu.length > 0) {
          e.push(
            <Box
              key={"content of " + lastCat}
              sx={{
                display: "flex",
                gap: 1,
                justifyContent: "flex-start",
                p: 2,
                flexWrap: "wrap",
                bgcolor: "action.hover",
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              {garundu}
            </Box>,
          );
          garundu = [];
        }

        for (let i = 0; i < cat.length; i++) {
          scat += (i === 0 ? "" : ";") + cat[i];
          lcat += lastCatArr.length > i ? (i === 0 ? "" : ";") + lastCatArr[i] : "";
          const mcat = scat + "";

          if (scat !== lcat) {
            s.push(e.length);

            const isOpen = open.includes(scat);
            const count = categoryCounts[scat];

            e.push(
              <Box
                key={scat}
                onClick={() => toggleOpen(mcat)}
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  py: 1.5,
                  pr: 1,
                  pl: "5px",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  userSelect: "none",
                  borderBottom: "0.75px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}

                <Typography
                  variant="subtitle2"
                  sx={{
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    flex: "0 1 auto",
                  }}
                >
                  {cat[i]}
                </Typography>

                {typeof count === "number" ? (
                  <Badge
                    badgeContent={count}
                    color="error"
                    sx={{
                      ml: 0.5,
                      "& .MuiBadge-badge": {
                        border: "1px solid",
                        borderColor: "background.paper",
                      },
                    }}
                  />
                ) : null}
              </Box>,
            );
          }

          if (!open.includes(scat)) {
            visible = false;
            break;
          }
        }

        lastCat = article.category;
        lastCatArr = cat.slice();
      }

      if (!visible) continue;

      garundu.push(
        <ArticleCard
          key={article.id}
          article={article}
          setSelection={(c: { [id: number]: number }) => setSelection((sel) => ({ ...sel, ...c }))}
          count={article.id in selection ? selection[article.id] : 0}
        />,
      );
    }

    if (garundu.length > 0) {
      e.push(
        <Box
          key={"content of " + lastCat}
          sx={{
            display: "flex",
            gap: 1,
            justifyContent: "center",
            p: 2,
            flexWrap: "wrap",
            bgcolor: "action.hover",
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          {garundu}
        </Box>,
      );
    }

    return [e, s] as const;
  }, [articles, open, selection, categoryCounts]);

  if ((bauhofArticleQuery as any).isLoadingError) {
    return <Typography>Fehler</Typography>;
  }

  if (bauhofArticleQuery.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <TextField
        sx={{ mt: 1 }}
        placeholder="Artikel filtern..."
        value={searchField}
        onChange={(e) => setSearchField(e.target.value)}
        inputMode="search"
        autoComplete="off"
        size="small"
      />

      <Box
        sx={{
          mt: 2,
          mb: 2,
          flex: 1,
          minHeight: 0,
          position: "relative",
          border: "0.75px solid",
          borderColor: "divider",
          overflow: "auto",
        }}
      >
        {elements}
      </Box>

      <Stack direction="row" spacing={1}>
        {abort ? (
          <Button variant="outlined" onClick={abort}>
            <ArrowBackIcon />
          </Button>
        ) : null}

        <Button
          variant="contained"
          color="success"
          fullWidth
          onClick={() => {
            onSelect(selectedArticles);
          }}
        >
          <CheckIcon />
        </Button>
      </Stack>
    </Box>
  );
}
